// Pure mortgage amortization math. No React, no I/O — everything here is a pure
// function of its inputs so the schedule can be computed live on the page and
// unit-tested in isolation.
//
// The schedule is derived entirely from the loan's terms (amount, rate, term,
// origination date). It does NOT require any recorded statements to exist — a
// user who has only entered their loan facts still gets a full payment forecast.

import { firstPaymentDate } from './dateHelpers';

/** Round to cents. Avoids floating-point drift accumulating across 360 rows. */
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Coerce to a finite number, or null. Rejects '', null, undefined, NaN. */
const num = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = typeof v === 'string' ? Number(v) : v;
    return Number.isFinite(n) ? n : null;
};

/**
 * Standard fixed-rate monthly principal + interest payment:
 *   M = P · r / (1 − (1 + r)^−n)   where r = annualRatePct / 100 / 12
 *
 * A 0% loan amortizes straight-line (P / n) — handled explicitly to avoid the
 * divide-by-zero the formula would otherwise hit.
 *
 * Returns the payment (a number) or null when any input is missing/invalid.
 */
export const monthlyPI = (principal, annualRatePct, termMonths) => {
    const P = num(principal);
    const rate = num(annualRatePct);
    const n = num(termMonths);
    if (P === null || rate === null || n === null) return null;
    if (P <= 0 || !Number.isInteger(n) || n <= 0 || rate < 0) return null;

    if (rate === 0) return round2(P / n);

    const r = rate / 100 / 12;
    const factor = Math.pow(1 + r, n);
    return round2((P * r * factor) / (factor - 1));
};

/** Add `n` whole months to a YYYY-MM-DD string, returning YYYY-MM-01 (UTC). */
const addMonths = (yyyymmdd, n) => {
    if (!yyyymmdd) return null;
    const [y, mo] = yyyymmdd.split('-').map(Number);
    const d = new Date(Date.UTC(y, mo - 1 + n, 1));
    const yy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${yy}-${mm}-01`;
};

/**
 * Run the amortization loop and return its rows + aggregate totals. Internal —
 * `buildAmortizationSchedule` wraps this to also compute the baseline savings.
 *
 * Escrow is informational — it's added to each row's `totalMonthly` but never
 * affects principal/interest/balance. Two modes:
 *   - flat: pass a numeric `escrow`; every row carries that same escrow.
 *   - breakdown: pass an `escrowResolver(dateStr, monthIndex) => {tax, insurance,
 *     pmi, hoa}`; each row computes its own components (e.g. taxes that change by
 *     year, insurance ÷12). When a resolver is given, `escrow` is ignored and the
 *     row gains an `escrowBreakdown` object.
 *
 * @param {number} P          starting principal balance
 * @param {number} rate       annual interest rate, percent
 * @param {number} n          scheduled term in months
 * @param {number} pi         scheduled monthly principal + interest
 * @param {number} extra      extra principal applied each month
 * @param {number} escrow     flat monthly escrow (used only when no resolver)
 * @param {string|null} first first payment date (YYYY-MM-01), or null
 * @param {Function|null} escrowResolver per-row escrow breakdown resolver, or null
 */
const runSchedule = (P, rate, n, pi, extra, escrow, first, escrowResolver = null) => {
    const monthlyRate = rate / 100 / 12;
    const rows = [];
    let balance = P;
    let totalInterest = 0;
    let totalPaid = 0;
    // Cap iterations defensively: extra payments only ever shorten the loan, so
    // the scheduled term is a safe upper bound (+1 for rounding on the tail).
    const maxIterations = n + 1;

    for (let i = 0; i < maxIterations && balance > 0.005; i += 1) {
        const interest = round2(balance * monthlyRate);
        // Scheduled principal = the regular payment's principal portion, plus any
        // extra. The final-payment branch keys off this (not the full payment) so
        // an early payoff's last payment never exceeds a regular one. The
        // `i === n - 1` clause forces the scheduled final month to settle even
        // when accumulated cent-rounding leaves the balance a few cents above the
        // scheduled principal — otherwise a standard loan spills a 1-cent extra
        // row past its `n`-month term.
        const scheduledPrincipal = round2(pi - interest) + extra;
        let principal;
        let payment;
        if (balance <= scheduledPrincipal || i === n - 1) {
            // Final payment: retire the whole remaining balance (the real-world
            // last payment trues up accumulated cent-rounding).
            principal = balance;
            payment = round2(balance + interest);
        } else {
            principal = scheduledPrincipal;
            payment = round2(pi + extra);
        }

        balance = round2(balance - principal);
        totalInterest = round2(totalInterest + interest);
        totalPaid = round2(totalPaid + payment);

        const date = first ? addMonths(first, i) : null;

        // Resolve this month's escrow. In breakdown mode each component is rounded
        // to cents independently so the displayed columns sum exactly to the total.
        let rowEscrow = escrow;
        let escrowBreakdown = null;
        if (escrowResolver) {
            const b = escrowResolver(date, i) || {};
            const tax = round2(Math.max(0, num(b.tax) ?? 0));
            const insurance = round2(Math.max(0, num(b.insurance) ?? 0));
            const pmi = round2(Math.max(0, num(b.pmi) ?? 0));
            const hoa = round2(Math.max(0, num(b.hoa) ?? 0));
            escrowBreakdown = { tax, insurance, pmi, hoa };
            rowEscrow = round2(tax + insurance + pmi + hoa);
        }

        rows.push({
            number: i + 1,
            date,
            payment,
            principal: round2(principal),
            interest,
            extraPrincipal: extra,
            escrow: rowEscrow,
            escrowBreakdown,
            totalMonthly: round2(payment + rowEscrow),
            balance: balance < 0 ? 0 : balance,
        });
    }

    return { rows, totalInterest, totalPaid };
};

/**
 * Build a full amortization schedule from a loan's terms.
 *
 * @param {Object} params
 * @param {number|string} params.originalLoanAmount   starting principal
 * @param {number|string} params.annualRatePct        annual interest rate, percent
 * @param {number|string} params.termMonths           loan term in months
 * @param {string} [params.originationDate]           YYYY-MM-DD (drives row dates)
 * @param {number|string} [params.extraMonthlyPrincipal=0]
 * @param {number|string} [params.escrowMonthly=0]    flat taxes/insurance per month
 * @param {Function} [params.escrowResolver]          per-row escrow breakdown
 *   resolver `(dateStr, monthIndex) => {tax, insurance, pmi, hoa}`. When given,
 *   `escrowMonthly` is ignored and each row carries an `escrowBreakdown`.
 * @returns {{rows: Array, summary: Object}|null}  null when required terms are missing
 */
export const buildAmortizationSchedule = ({
    originalLoanAmount,
    annualRatePct,
    termMonths,
    originationDate,
    extraMonthlyPrincipal = 0,
    escrowMonthly = 0,
    escrowResolver = null,
} = {}) => {
    const P = num(originalLoanAmount);
    const rate = num(annualRatePct);
    const n = num(termMonths);
    if (P === null || rate === null || n === null) return null;
    if (P <= 0 || !Number.isInteger(n) || n <= 0 || rate < 0) return null;

    const pi = monthlyPI(P, rate, n);
    if (pi === null) return null;

    // Round to cents up front so a pasted >2-decimal input can't introduce
    // float artifacts that accumulate across the schedule.
    const extra = round2(Math.max(0, num(extraMonthlyPrincipal) ?? 0));
    const resolver = typeof escrowResolver === 'function' ? escrowResolver : null;
    const escrow = round2(Math.max(0, num(escrowMonthly) ?? 0));
    const first = firstPaymentDate(originationDate);

    const { rows, totalInterest, totalPaid } = runSchedule(P, rate, n, pi, extra, escrow, first, resolver);

    // Escrow now varies by month (taxes change per year), so the summary reports
    // the FIRST month's escrow as the representative "current" monthly figure.
    const firstEscrow = rows.length ? rows[0].escrow : escrow;
    const summary = {
        monthlyPI: pi,
        escrowMonthly: firstEscrow,
        escrowBreakdown: rows.length ? rows[0].escrowBreakdown : null,
        totalMonthly: round2(pi + extra + firstEscrow),
        payoffDate: rows.length ? rows[rows.length - 1].date : null,
        monthsToPayoff: rows.length,
        totalInterest,
        totalPaid,
    };

    // When extra payments are in play, compare against the no-extra baseline so
    // the UI can show interest saved and how many months earlier it pays off.
    if (extra > 0) {
        const baseline = runSchedule(P, rate, n, pi, 0, escrow, first);
        summary.baselineMonths = baseline.rows.length;
        summary.baselineTotalInterest = baseline.totalInterest;
        summary.monthsSaved = baseline.rows.length - rows.length;
        summary.interestSaved = round2(baseline.totalInterest - totalInterest);
    }

    return { rows, summary };
};
